import { keepPreviousData, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { isDemoMode } from "../config/runtime";
import { countryName } from "../lib/country-names";
import { useAuthorityMutation } from "./authority-mutation";
import type {
  CanonicalCityCreateInput,
  CanonicalCityUpdateInput,
  DestinationCreateInput,
  DestinationLifecycleInput,
  DestinationUpdateInput,
  ExperienceFilters,
  ReferenceFilters,
  ReferenceKind,
  ReferenceOption,
} from "./catalogue-models";
import { apiClient } from "./client";
import { demoProfileOptions } from "./demo-profile-options";
import type { PageResult } from "./models";
import { queryKeys } from "./query-keys";
import { adminRepository } from "./repository";
import { throwApiError } from "./response-error";
import { sessionHeaders } from "./session-headers";

export function useCanonicalCities(filters: ReferenceFilters) {
  return useQuery({
    queryKey: queryKeys.cataloguePage("cities", filters),
    queryFn: ({ signal }) => adminRepository.listCanonicalCities(filters, signal),
    placeholderData: keepPreviousData,
  });
}
export function useCanonicalCity(id: string) {
  return useQuery({
    queryKey: queryKeys.catalogueDetail("cities", id),
    queryFn: ({ signal }) => adminRepository.getCanonicalCity(id, signal),
    enabled: Boolean(id),
  });
}
export function useDestinationReferences(filters: ReferenceFilters) {
  return useQuery({
    queryKey: queryKeys.cataloguePage("destinations", filters),
    queryFn: ({ signal }) => adminRepository.listDestinationReferences(filters, signal),
    placeholderData: keepPreviousData,
  });
}
export function useDestinationReference(id: string) {
  return useQuery({
    queryKey: queryKeys.catalogueDetail("destinations", id),
    queryFn: ({ signal }) => adminRepository.getDestinationReference(id, signal),
    enabled: Boolean(id),
  });
}
export function useSystemExperiences(filters: ExperienceFilters) {
  return useQuery({
    queryKey: queryKeys.cataloguePage("experiences", filters),
    queryFn: ({ signal }) => adminRepository.listSystemExperiences(filters, signal),
    placeholderData: keepPreviousData,
  });
}
export function useSystemExperience(id: string) {
  return useQuery({
    queryKey: queryKeys.catalogueDetail("experiences", id),
    queryFn: ({ signal }) => adminRepository.getSystemExperience(id, signal),
    enabled: Boolean(id),
  });
}

/** The scoped Activity-photo projection for this Admin Experience detail. */
export function useExperienceGallery(id: string, page: number, enabled: boolean) {
  return useQuery({
    queryKey: ["member-catalogue", "experience-gallery", id, page],
    enabled: enabled && Boolean(id),
    queryFn: async ({ signal }) => {
      const result = await apiClient.GET("/v1/admin/experiences/{experienceId}/gallery", {
        params: {
          header: await sessionHeaders(),
          path: { experienceId: id },
          query: { page, pageSize: 8 },
        },
        signal,
      });
      if (!result.data) return throwApiError(result.error, result.response);
      return result.data;
    },
  });
}

/** Resolve only the references on the current bounded page, including records beyond page one. */
export function useReferencedExperiences(ids: string[], enabled: boolean) {
  const queries = useQueries({
    queries: [...new Set(enabled ? ids : [])].map((id) => ({
      queryKey: queryKeys.catalogueDetail("experiences", id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        adminRepository.getSystemExperience(id, signal),
    })),
  });
  return {
    data: queries.flatMap((query) => (query.data && !query.isError ? [query.data] : [])),
    isError: queries.some((query) => query.isError),
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
}

export function useReferencedDestinations(ids: string[], enabled: boolean) {
  const queries = useQueries({
    queries: [...new Set(enabled ? ids : [])].map((id) => ({
      queryKey: queryKeys.catalogueDetail("destinations", id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        adminRepository.getDestinationReference(id, signal),
    })),
  });
  return {
    data: queries.flatMap((query) => (query.data && !query.isError ? [query.data] : [])),
    isError: queries.some((query) => query.isError),
    refetch: () => Promise.all(queries.map((query) => query.refetch())),
  };
}
/** Read one selected record on restoration; never show an internal ID as its label. */
export function useCatalogueSelection(kind: ReferenceKind, id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.catalogueDetail(kind, id), "selection"],
    enabled: enabled && Boolean(id),
    queryFn: async ({ signal }): Promise<ReferenceOption> => {
      if (kind === "cities") {
        const row = await adminRepository.getCanonicalCity(id, signal);
        return {
          id: row.id,
          label: `${row.displayName}, ${countryName(row.countryCode)}`,
          version: row.version,
          timeZone: row.timeZone,
        };
      }
      if (kind === "destinations") {
        const row = await adminRepository.getDestinationReference(id, signal);
        return {
          id: row.id,
          label: `${row.city}, ${countryName(row.countryCode)}`,
          version: row.version,
          timeZone: row.timeZone,
        };
      }
      const row = await adminRepository.getSystemExperience(id, signal);
      return { id: row.id, label: row.canonicalTitle, version: row.version };
    },
  });
}

export function useCatalogueLookup(
  kind: ReferenceKind,
  filters: ReferenceFilters & { destinationId?: string },
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.catalogueLookup(kind, filters),
    enabled,
    queryFn: async ({ signal }): Promise<PageResult<ReferenceOption>> => {
      if (kind === "cities") {
        const page = await adminRepository.listCanonicalCities(filters, signal);
        return {
          ...page,
          items: page.items.map((city) => ({
            id: city.id,
            label: `${city.displayName}, ${countryName(city.countryCode)}`,
            version: city.version,
            timeZone: city.timeZone,
          })),
        };
      }
      if (kind === "destinations") {
        const page = await adminRepository.listDestinationReferences(filters, signal);
        return {
          ...page,
          items: page.items.map((destination) => ({
            id: destination.id,
            label: `${destination.city}, ${countryName(destination.countryCode)}`,
            version: destination.version,
            timeZone: destination.timeZone,
          })),
        };
      }
      const page = await adminRepository.listSystemExperiences(filters, signal);
      return {
        ...page,
        items: page.items.map((experience) => ({
          id: experience.id,
          label: `${experience.canonicalTitle} · ${experience.lifecycleStatus}`,
          version: experience.version,
        })),
      };
    },
  });
}

function useReferenceMutation<Input, Output>(run: (input: Input) => Promise<Output>) {
  const client = useQueryClient();
  return useAuthorityMutation({
    mutationFn: run,
    onSuccess: async () => {
      await Promise.all(
        [
          "cities",
          "destinations",
          "experiences",
          "partners",
          "partner-offerings",
          "audit-trail",
        ].map((root) => client.invalidateQueries({ queryKey: [root] })),
      );
    },
  });
}
export const useCreateCanonicalCity = () =>
  useReferenceMutation((input: CanonicalCityCreateInput) =>
    adminRepository.createCanonicalCity(input),
  );
export const useUpdateCanonicalCity = (id: string) =>
  useReferenceMutation((input: CanonicalCityUpdateInput) =>
    adminRepository.updateCanonicalCity(id, input),
  );
export const useCreateDestinationReference = () =>
  useReferenceMutation((input: DestinationCreateInput) =>
    adminRepository.createDestinationReference(input),
  );
export const useUpdateDestinationReference = (id: string) =>
  useReferenceMutation((input: DestinationUpdateInput) =>
    adminRepository.updateDestinationReference(id, input),
  );
export const useDestinationLifecycle = (id: string) =>
  useReferenceMutation((input: DestinationLifecycleInput) =>
    adminRepository.updateDestinationLifecycle(id, input),
  );

/** Canonical options shared with the mobile profile; no separate catalogue vocabulary. */
export function useCatalogueContentOptions() {
  return useQuery({
    queryKey: ["catalogue-content-options"],
    queryFn: async ({ signal }) => {
      if (isDemoMode) return demoProfileOptions;
      const result = await apiClient.GET("/v1/profile-options", { signal });
      if (!result.data) return throwApiError(result.error, result.response);
      return result.data;
    },
  });
}
